'use client';

import { useMemo, useState } from 'react';
import { Server, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n/useTranslation';
import useStrategies from '@/components/predic/hooks/use-strategies';
import type { ShadowRunStatus } from '@/lib/backtest/types';
import type { LiveRun } from '@/lib/live-trading/types';
import {
  formatMoneyString,
  formatPct,
  parseDecimalString,
  stripTrailingZero,
} from '@/components/backtest/utils';
import Ping from '@/components/common/Ping';
import Link from 'next/link';
import { formatTimeAgo } from '@/lib/utils/date';
import Empty from '../ui/Empty';

/**
 * 策略面板：Active/Finished 两个 tab + 策略运行卡列表。
 * 数据来自 useStrategies（GET /api/live-trading/runs，实盘 run 全量列表）。
 * Figma: Pythra 产品版本管理 node 1167-86106
 */

export type StrategyRunStatus =
  | 'scheduled'
  | 'running'
  | 'paused'
  | 'stopped'
  | 'finished'
  | 'failed';

const STATUS_COLOR: Record<StrategyRunStatus, string> = {
  scheduled: 'text-[#E6C07B]',
  running: 'text-[#14C784]',
  paused: 'text-[#E6C07B]',
  stopped: 'text-[#B7B9BC]',
  finished: 'text-[#60a5fa]',
  failed: 'text-[#f87171]',
};

const STATUS_DOT: Record<StrategyRunStatus, string> = {
  scheduled: 'bg-[#E6C07B]',
  running: 'bg-[#14C784] ring-4 ring-[#14C784]/30',
  paused: 'bg-[#E6C07B]',
  stopped: 'bg-[#B7B9BC]',
  finished: 'bg-[#60a5fa]',
  failed: 'bg-[#f87171]',
};

/** ShadowRunStatus → 面板展示状态 */
const RUN_STATUS_MAP: Record<ShadowRunStatus, StrategyRunStatus> = {
  scheduled: 'scheduled',
  active: 'running',
  paused: 'paused',
  stopped: 'stopped',
  completed: 'finished',
  failed: 'failed',
};

/** 进行中（Active tab）的状态；其余归入 Finished tab */
const ACTIVE_STATUSES: StrategyRunStatus[] = ['scheduled', 'running', 'paused'];

export interface StrategyRunItem {
  id: string;
  /** 所属策略会话 id（跳转 pythra-forge 会话页用） */
  session_id?: string;
  /** 策略名，如 'Czechia vs Korea Republic-Back to Lay' */
  title: string;
  /** 关联事件名，如 'Brazil vs Scotland' */
  eventName?: string;
  status: StrategyRunStatus;
  /** 是否实盘（Live Trading 徽标） */
  live?: boolean;
  /** 展示金额，如 '+$24,775.12'；颜色按符号推断 */
  unrealizedPnl?: string;
  /** 展示百分比，如 '144.4%' */
  pnlPercent?: string;
  /** 持仓金额展示文本，如 '$1340.51' */
  positions?: string;
  /** 事件进度展示文本，如 '44%' */
  eventProgress?: string;
  /** 心跳展示文本，如 '21 sec ago' */
  heartbeat?: string;
  /** 成交笔数 */
  trades?: number | string;
}

export interface StrategyPanelProps {
  onSelect?: (item: StrategyRunItem) => void;
  className?: string;
}

/** 实盘 run → 卡片数据（ShadowRunSummary 全量字段直接映射） */
function toRunItem(run: LiveRun): StrategyRunItem {
  const unrealizedPnl = parseDecimalString(run.unrealized_pnl);
  const returnPct = parseDecimalString(run.return_pct);
  const progress = parseDecimalString(run.event_progress_pct);
  const positions = parseDecimalString(run.positions_value);

  return {
    id: run.shadow_run_id,
    session_id: run.session_id,
    title: run.strategy_title || run.live_event_slug,
    eventName: run.event_title ?? undefined,
    status: RUN_STATUS_MAP[run.status] ?? 'stopped',
    // 该接口只返回 mode=live 的 run；字段缺省（旧后端）也视为实盘
    live: run.mode !== 'paper',
    unrealizedPnl: unrealizedPnl != null ? formatMoneyString(run.unrealized_pnl) : undefined,
    pnlPercent: returnPct != null ? formatPct(returnPct, 1, false) : undefined,
    positions:
      positions != null
        ? `$${positions.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : undefined,
    eventProgress: progress != null ? `${stripTrailingZero(progress.toFixed(0))}%` : undefined,
    heartbeat: run.last_heartbeat_at ? formatTimeAgo(run.last_heartbeat_at) : undefined,
    trades: run.trade_count,
  };
}

function pnlTone(pnl?: string) {
  if (pnl?.startsWith('+')) return 'text-[#14C784]';
  if (pnl?.startsWith('-')) return 'text-[#FF2D2D]';
  return 'text-white';
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs leading-5 text-[#B7B9BC]">{label}</span>
      <span className="text-sm leading-5 text-white">{value}</span>
    </div>
  );
}

function StrategyRunCard({ item, onSelect }: { item: StrategyRunItem; onSelect?: () => void }) {
  const { t } = useTranslation();
  const statusLabel: Record<StrategyRunStatus, string> = {
    scheduled: t('Scheduled'),
    running: t('Running'),
    paused: t('Paused'),
    stopped: t('Stopped'),
    finished: t('Finished'),
    failed: t('Failed'),
  };

  const metrics = [
    item.positions !== undefined && { label: t('Positions'), value: item.positions },
    item.eventProgress !== undefined && { label: t('Event Progress'), value: item.eventProgress },
    item.heartbeat !== undefined && { label: t('Heartbeat'), value: item.heartbeat },
    item.trades !== undefined && { label: t('Trades'), value: String(item.trades) },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <Link href={`/predic/pythra-forge/${item.session_id}`}>
      <article
        className={cn(
          'flex flex-col gap-3 rounded-[11px] border border-[#1E2126] bg-[#131518] p-3',
          onSelect && 'cursor-pointer transition-colors hover:border-[#2A2E35]',
        )}
        onClick={onSelect}
      >
        {/* Live 徽标 + 运行状态 + 加载图标 */}
        <div className="flex items-center justify-between gap-3 flex-1 w-full">
          {item.live && (
            <span className="flex h-5 items-center gap-1 rounded-full bg-[#14C784]/15 px-2 text-xs leading-5 text-[#14C784]">
              <Zap size={14} className="fill-[#14C784]" strokeWidth={0} />
              {t('Live Trading')}
            </span>
          )}
          {item.status !== 'running' ? (
            <span
              className={cn('flex items-center gap-2 text-xs leading-5', STATUS_COLOR[item.status])}
            >
              <span className={cn('size-1.5 rounded-full', STATUS_DOT[item.status])} />
              {statusLabel[item.status]}
            </span>
          ) : (
            <div className="flex items-center gap-2 text-[#14C784] text-xs">
              <Ping pingColor="#14C784" />
              {statusLabel[item.status]}
            </div>
          )}
        </div>

        <div className="h-px bg-[#1F1F1F] -mx-3" />
        {/* 标题 + 事件名 */}
        <div className="flex flex-col gap-2">
          <h3 className="truncate text-base font-medium text-white">{item.title}</h3>
          {item.eventName && (
            <span className="flex items-center gap-1 text-xs leading-4.5 text-[#B7B9BC]">
              <Server size={16} strokeWidth={1.5} className="rotate-180" />
              {item.eventName}
            </span>
          )}
        </div>

        {/* PnL 统计块 */}
        <div className="flex flex-col gap-2 rounded-[10px] border border-white/10 bg-black p-3">
          {item.unrealizedPnl && (
            <div
              className={cn(
                'flex items-center gap-2 text-sm leading-5.5',
                pnlTone(item.unrealizedPnl),
              )}
            >
              <span>
                {t('Unrealized PnL')} {item.unrealizedPnl}
              </span>
              {item.pnlPercent && (
                <span className="flex items-center gap-1">
                  （
                  <span className="text-[9px] leading-none">
                    {item.unrealizedPnl.startsWith('-') ? '▼' : '▲'}
                  </span>
                  {item.pnlPercent}）
                </span>
              )}
            </div>
          )}
          {metrics.length > 0 && (
            <div className="flex flex-wrap items-center gap-4">
              {metrics.map((m, i) => (
                <div key={m.label} className="flex items-center gap-4">
                  {i > 0 && <span className="h-5 w-px bg-white/25" />}
                  <Metric label={m.label} value={m.value} />
                </div>
              ))}
            </div>
          )}
        </div>
      </article>
    </Link>
  );
}

export default function StrategyPanel({ onSelect, className }: StrategyPanelProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'active' | 'finished'>('active');

  const { strategies, isLoading } = useStrategies();

  const { active, finished } = useMemo(() => {
    const items = (strategies ?? []).map(toRunItem);
    return {
      active: items.filter((item) => ACTIVE_STATUSES.includes(item.status)),
      finished: items.filter((item) => !ACTIVE_STATUSES.includes(item.status)),
    };
  }, [strategies]);
  const list = tab === 'active' ? active : finished;

  const tabs = [
    { key: 'active' as const, label: t('Active') },
    { key: 'finished' as const, label: t('Finished') },
  ];

  return (
    <section className={cn('flex flex-col gap-4', className)}>
      <h2 className="text-lg font-semibold text-white">{t('Strategies')}</h2>
      <div className="flex items-center gap-2.5">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'flex h-7 w-20 items-center justify-center rounded-md text-sm font-medium transition-colors',
              tab === key
                ? 'bg-[#A772FF]/25 text-[#A772FF]'
                : 'bg-[#262626] text-[#B7B9BC] hover:text-white',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2.5">
        {isLoading && list.length === 0 ? (
          <div className="space-y-2.5">
            {[0, 1].map((i) => (
              <div key={i} className="h-45 animate-pulse rounded-[11px] bg-[#131518]" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <Empty
            title={t('No Active Strategies')}
            description={t('Choose a strategy and let it run automatically after funding.')}
          />
        ) : (
          // <p className="py-10 text-center text-sm text-[#757575]">{t('No strategies yet')}</p>
          list.map((item) => (
            <StrategyRunCard
              key={item.id}
              item={item}
              onSelect={onSelect && (() => onSelect(item))}
            />
          ))
        )}
      </div>
    </section>
  );
}
